type RsaPublicKey = {
  modulus: bigint;
  exponent: bigint;
  byteLength: number;
};

function base64ToBytes(value: string) {
  const binary = window.atob(value.replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function bytesToBigInt(bytes: Uint8Array) {
  let hex = '';

  bytes.forEach((byte) => {
    hex += byte.toString(16).padStart(2, '0');
  });

  return BigInt(`0x${hex || '0'}`);
}

function bigIntToBytes(value: bigint, length: number) {
  let hex = value.toString(16);

  if (hex.length % 2) hex = `0${hex}`;

  const bytes = new Uint8Array(length);
  const valueBytes = hex.match(/.{1,2}/g)?.map((item) => parseInt(item, 16)) ?? [];
  const offset = Math.max(0, length - valueBytes.length);

  valueBytes.slice(-length).forEach((byte, index) => {
    bytes[offset + index] = byte;
  });

  return bytes;
}

function modPow(base: bigint, exponent: bigint, modulus: bigint) {
  if (modulus === BigInt(1)) return BigInt(0);

  let result = BigInt(1);
  let nextBase = base % modulus;
  let nextExponent = exponent;

  while (nextExponent > 0) {
    if (nextExponent % BigInt(2) === BigInt(1)) {
      result = (result * nextBase) % modulus;
    }

    nextExponent /= BigInt(2);
    nextBase = (nextBase * nextBase) % modulus;
  }

  return result;
}

class DerReader {
  private offset = 0;

  constructor(private readonly bytes: Uint8Array) {}

  readByte() {
    const byte = this.bytes[this.offset];
    this.offset += 1;
    return byte;
  }

  readLength() {
    const first = this.readByte();

    if (first < 0x80) return first;

    const lengthBytes = first & 0x7f;
    let length = 0;

    for (let index = 0; index < lengthBytes; index += 1) {
      length = length * 256 + this.readByte();
    }

    return length;
  }

  readElement(expectedTag?: number) {
    const tag = this.readByte();
    const length = this.readLength();

    if (typeof expectedTag === 'number' && tag !== expectedTag) {
      throw new Error('INVALID_DER_TAG');
    }

    const start = this.offset;
    const end = start + length;
    this.offset = end;

    return this.bytes.slice(start, end);
  }
}

function readInteger(reader: DerReader) {
  const bytes = reader.readElement(0x02);
  return bytes[0] === 0 ? bytes.slice(1) : bytes;
}

function parsePublicKey(publicKeyBase64: string): RsaPublicKey {
  const spkiReader = new DerReader(base64ToBytes(publicKeyBase64));
  const spki = new DerReader(spkiReader.readElement(0x30));

  spki.readElement(0x30);
  const bitString = spki.readElement(0x03);
  const publicKeyReader = new DerReader(bitString.slice(1));
  const publicKeySequence = new DerReader(publicKeyReader.readElement(0x30));
  const modulusBytes = readInteger(publicKeySequence);
  const exponentBytes = readInteger(publicKeySequence);

  return {
    modulus: bytesToBigInt(modulusBytes),
    exponent: bytesToBigInt(exponentBytes),
    byteLength: modulusBytes.length,
  };
}

export function rsaPublicDecryptPkcs1(
  encryptedBase64: string,
  publicKeyBase64: string,
) {
  const publicKey = parsePublicKey(publicKeyBase64);
  const encrypted = bytesToBigInt(base64ToBytes(encryptedBase64));
  const decrypted = modPow(
    encrypted,
    publicKey.exponent,
    publicKey.modulus,
  );
  const block = bigIntToBytes(decrypted, publicKey.byteLength);

  if (block[0] !== 0x00 || block[1] !== 0x01) {
    throw new Error('INVALID_RSA_BLOCK');
  }

  let separatorIndex = 2;

  while (separatorIndex < block.length && block[separatorIndex] === 0xff) {
    separatorIndex += 1;
  }

  if (block[separatorIndex] !== 0x00) {
    throw new Error('INVALID_RSA_PADDING');
  }

  return new TextDecoder().decode(block.slice(separatorIndex + 1));
}
